/**
 * UsageBadge — predictive paywall surface. Reads /api/coach/usage and
 * shows a small "Nx left this month" hint next to free-tier action
 * buttons (Record, Generate flashcards, Open Study Buddy).
 *
 * Three visual states:
 *   • plenty (>20% remaining)         → quiet "Nx left" pill
 *   • near (≤20% remaining)           → amber "1 of 5 left" pill
 *   • at_limit (used == cap)          → red "Upgrade" pill linking to /pricing
 *
 * Self-hides for unlimited tiers (Scholar/Pro) so we never nag paying
 * users about non-existent caps. Single TanStack-Query under the hood
 * so multiple badges on the same page only fire one round-trip.
 *
 * Usage:
 *   <UsageBadge resource="lectures" />
 *   <UsageBadge resource="flashcards" inline />
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABEL = {
  lectures:    { unit: 'lecture',   period: 'month' },
  flashcards:  { unit: 'flashcard', period: 'total' },
  ai_sessions: { unit: 'AI chat',   period: 'month' },
};

const useUsage = () => useQuery({
  queryKey: ['coach', 'usage'],
  queryFn: async () => (await axios.get(`${API}/coach/usage`)).data,
  staleTime: 60_000,
  refetchOnWindowFocus: false,
});

const UsageBadge = ({ resource = 'lectures', inline = false, className = '' }) => {
  const { data, isLoading } = useUsage();
  if (isLoading || !data) return null;
  const meta = data[resource];
  if (!meta || meta.limit === 'unlimited') return null;

  const { used, limit, remaining, near_limit, at_limit } = meta;
  const label = LABEL[resource] || { unit: 'item', period: 'month' };
  const tier = data.tier || 'free';

  if (at_limit) {
    return (
      <Link
        to="/pricing"
        className={`inline-flex items-center gap-1.5 text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 px-2.5 py-1 rounded-full hover:bg-red-100 transition-colors ${className}`}
        data-testid={`usage-badge-${resource}-atlimit`}
        title={`You've used all ${limit} ${label.unit}s this ${label.period}`}
      >
        <Lock className="w-3 h-3" /> Upgrade — {limit}/{limit} used
      </Link>
    );
  }

  if (near_limit) {
    return (
      <Link
        to="/pricing"
        className={`inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full hover:bg-amber-100 transition-colors ${className}`}
        data-testid={`usage-badge-${resource}-near`}
        title={`${remaining} of ${limit} ${label.unit}s left — tap to compare plans`}
      >
        {remaining} of {limit} {label.unit}{remaining === 1 ? '' : 's'} left
      </Link>
    );
  }

  return (
    <span
      className={`${inline ? 'inline-flex' : 'inline-flex'} items-center text-[11px] text-[var(--isp-muted)] font-medium ${className}`}
      data-testid={`usage-badge-${resource}-plenty`}
      title={`${used} of ${limit} used this ${label.period} (${tier} tier)`}
    >
      {used}/{limit} this {label.period}
    </span>
  );
};

export default UsageBadge;
